import {
  Button,
  Grid,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { DateRangePicker } from '@mantine/dates';
import { FormList, formList, joiResolver, useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { CalendarMinus, X } from 'tabler-icons-react';
import { CurrenciesData } from '../../data/currencies-data.array';
import {
  SettlementFileNames,
  SettlementFileNamesReadable,
} from '../../enums/settlement-file-names';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { CreateSettlementSchema } from '../../schemas/create-settlement-request.schema';
import { SettlementFileMetadata } from '../../type/admin.types';
import {
  CreateSettlementRequest,
  CreateSettlementRequestFrontEnd,
} from '../../type/create-soa-details-request.type';
import { CreateSettlementResponse } from '../../type/create-soa-details-response.types';
import { ErrorResponse } from '../../type/error-response.types';
import { EntityFilterData, EntityIdFilterData } from '../../type/filter.types';
import FileUpload from '../buttons/file-upload.button/file-upload.button';
import SelectBranchFilter from '../filters/select-branch-filter.component';
import SelectMerchantFilter from '../filters/select-merchant-filter.component';

interface Props {
  isAddSOAModalOpen: boolean;
  setIsAddSOAModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

type Files = {
  [type in SettlementFileNames]?: FormList<never> | FormList<any>;
};

function AddSOADetailsModal(props: Props) {
  const theme = useMantineTheme();
  const axiosPrivate = useAxiosPrivate();
  const { isAddSOAModalOpen, setIsAddSOAModalOpen, setRefreshTable } = props;
  const [isLoading, setIsLoading] = useState(false);
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      merchant: '',
      branch: '',
    },
  });

  const handleClose = () => {
    setIsAddSOAModalOpen(false);
    filterValues.reset();
    form.reset();
  };

  const form = useForm<CreateSettlementRequestFrontEnd>({
    initialValues: {
      currencyId: null,
      payoutAmount: null,
      transactionDate: [null, null],
      merchantId: '',
      branchId: '',
      soa: formList([]),
    },
    schema: joiResolver(CreateSettlementSchema),
  });

  const placeholders = {
    currency: 'Select currency',
    payoutAmount: 'Enter payout amount',
    merchant: 'Select merchant',
    branch: 'Select branch',
  };

  const labels = {
    currency: 'Currency',
    payoutAmount: 'Payout amount',
    transactionDate: 'Transaction date',
    merchant: 'Merchant',
    branch: 'Branch',
  };

  useEffect(() => {
    form.setFieldValue('merchantId', filterValues.values.merchant ?? '');
  }, [filterValues.values.merchant]);

  useEffect(() => {
    form.setFieldValue('branchId', filterValues.values.branch ?? '');
  }, [filterValues.values.branch]);

  const handleSubmit = async () => {
    const createSettlementRequest: CreateSettlementRequest = {
      currencyId: form.values.currencyId!,
      payoutAmount: form.values.payoutAmount!,
      transactionDateFrom: form.values.transactionDate[0]!,
      transactionDateTo: form.values.transactionDate[1]!,
      merchantId: form.values.merchantId,
      branchId: form.values.branchId,
    };

    let data: SettlementFileMetadata[] = [];
    const formData = new FormData();

    const { soa } = form.values;
    const files: Files = { soa };

    files[SettlementFileNames.SOA]?.forEach((file: File) => {
      file !== undefined &&
        file.size > 0 &&
        formData.append(SettlementFileNames.SOA, new Blob([file]), file.name);
    });

    try {
      setIsLoading(true);

      const response = await axiosPrivate.post(
        'web/settlements/create',
        createSettlementRequest
      );

      const createSettlementResponse: CreateSettlementResponse = response.data;

      const settlementId = createSettlementResponse.settlementId;

      await axiosPrivate.post(
        `/web/settlements/${settlementId}/files`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      setRefreshTable((prev) => !prev);

      handleClose();
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-create-settlement',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        size='xl'
        opened={isAddSOAModalOpen}
        centered
        onClose={handleClose}
        closeOnClickOutside={isLoading ? false : true}
        closeOnEscape={isLoading ? false : true}
      >
        <Stack p='xl'>
          <Group>
            <Title>Add SOA details</Title>
          </Group>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Grid>
              <Grid.Col>
                <Select
                  placeholder={placeholders.currency}
                  label={labels.currency}
                  value={
                    form.values.currencyId
                      ? form.values.currencyId.toString()
                      : ''
                  }
                  data={CurrenciesData}
                  onChange={(value: string) => {
                    form.setFieldValue('currencyId', parseInt(value));
                  }}
                  error={form.errors.currencyId}
                />
              </Grid.Col>
              <Grid.Col>
                <NumberInput
                  label={labels.payoutAmount}
                  placeholder={placeholders.payoutAmount}
                  {...form.getInputProps('payoutAmount')}
                  precision={2}
                  hideControls
                />
              </Grid.Col>
              <Grid.Col>
                <DateRangePicker
                  fullWidth
                  label={labels.transactionDate}
                  dayStyle={(date) =>
                    date.getDate() === new Date().getDate() &&
                    date.getMonth() === new Date().getMonth() &&
                    (form.values.transactionDate.length !== 2 ||
                      (form.values.transactionDate[0] === null &&
                        form.values.transactionDate[1] === null))
                      ? {
                          backgroundColor: theme.colors.blue[6],
                          color: theme.white,
                        }
                      : {}
                  }
                  onChange={(date) => {
                    form.setFieldValue('transactionDate', date);
                  }}
                  icon={
                    <CalendarMinus
                      size={20}
                      strokeWidth={2}
                      color={'#a4a0a0'}
                    />
                  }
                  value={form.values.transactionDate}
                  error={form.errors.transactionDate}
                  allowSingleDateInRange
                  clearable
                />
              </Grid.Col>
              <Grid.Col>
                <SelectMerchantFilter
                  filterSelectData={filterSelectData}
                  filterValues={filterValues}
                  error={form.errors.merchantId}
                />
              </Grid.Col>
              <Grid.Col>
                <SelectBranchFilter
                  filterSelectData={filterSelectData}
                  filterValues={filterValues}
                  isMerchantDependent
                  error={form.errors.branchId}
                />
              </Grid.Col>
              <Grid.Col>
                <Text style={{ fontSize: 14 }}>
                  {SettlementFileNamesReadable.SOA}
                </Text>
                <FileUpload form={form} value={SettlementFileNames.SOA} />
                {form.errors.soa && <Text color='red'>{form.errors.soa}</Text>}
              </Grid.Col>
              <Grid.Col>
                <Button type='submit' fullWidth loading={isLoading}>
                  Confirm
                </Button>
              </Grid.Col>
            </Grid>
          </form>
        </Stack>
      </Modal>
    </>
  );
}

export default AddSOADetailsModal;
